import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from 'reactstrap';
import './VisitReportsPDF.scss';
import { useTranslation } from 'react-i18next';

import { TrapTimeLineChart } from './Charts/TrapsTimeLineChart';
import { SBSChart } from './Charts/SBSChart';
import HeatMaps from './PDFComponents/HeatMaps';

import { getVisitReports } from 'api/visitReports/getVisitReports';
import { MLButton } from 'components/Button/Button';
import useSignVisitByEmployee from 'hooks/VisitReport/useSignVisitByEmployee';
import useGetUser from 'hooks/VisitReport/useGetUser';
import { SignalBaitData, SignalData, SignalTimeData } from 'api/visitReports/type';
import { useManageSignatureModalStore } from 'components/SignatureModal/ManageSignatureModalStore';
import SignatureModal from 'components/SignatureModal/SignatureModal';
import { TrapTypeMonitoring } from 'api/reports/types';
import trojanLogo from 'assets/images/trojan_logo.svg';
import InnerSTMonitoring from 'pages/PeriodReports/components/InnerSTMonitoring';
import { TrapType } from 'components/AnalyticsPage/TrapType';

const VisitDocsPDF = () => {
  const { onManageSignatureModalOpen } = useManageSignatureModalStore();
  let { slug, id } = useParams();
  const navigate = useNavigate();
  const { data: userData, refetch } = useGetUser();
  const { t } = useTranslation();
  const { singVisitByEmployee } = useSignVisitByEmployee();
  const { data, isLoading } = useQuery('visitDocs', () => getVisitReports({ slug: slug || '', id: id || '' }), {});
  const printPDF = () => {
    window.print();
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        @page { margin: 0; size: portrait; }
        
        body * {
          visibility: hidden;
        }
        
        #reportPrintPDF, #reportPrintPDF * {
          visibility: visible;
        }
        
        #reportPrintPDF {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          margin: 0;
        }
        
        .row-print {
          display: flex;
          justify-content: center;
          margin: 0;
        }
        
        .col-print {
          flex: 0 0 66.666667%;
          max-width: 66.666667%;
          margin: 0;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="page-content bg-light">
      <Container fluid>
        <MLButton
          innerText={t('VisitReportsPDF.buttons.print-pdf')}
          margin={1}
          color="success"
          fontSize={18}
          icon="ri-file-pdf-fill fs-16"
          onClick={printPDF}
        />
        <MLButton
          innerText={t('VisitReportsPDF.buttons.back-to-docs')}
          margin={1}
          color="info"
          fontSize={18}
          icon="ri-file-pdf-fill fs-16"
          onClick={() => {
            navigate(`/visit-report/${slug}/${id}/`);
          }}
        />
        <div className="card-body-ml" id="reportPrintPDF">
          <div className="row">
            <div className="col-lg-9">
              <span className="logo-lg">
                <img src={trojanLogo} alt="" height="32" />
              </span>
            </div>
            <div className="col-lg-3" style={{ borderBottom: '1px solid black', margin: '10px 0' }} />
          </div>
          <div className="row mt-5">
            <div className="header-container">
              <h3>
                <strong>
                  {t('VisitReportsPDF.header.title')} {id}
                </strong>
              </h3>
              <h5 className="white-space: nowrap; margin-left: 10px;">
                &nbsp; {t('VisitReportsPDF.header.title-2')} {data?.start_date} - {data?.end_date}
              </h5>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-8">
              <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle vertical-table">
                <tbody className="list form-check-all">
                  <tr>
                    <th scope="row">{t('VisitReportsPDF.header.visit-date')}</th>
                    <td className="text-start p-1 m-0">
                      {data?.timer_start ? (
                        dayjs(data?.timer_start).format('YYYY-MM-DD')
                      ) : (
                        <strong>
                          <u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u>.<u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u>.
                          <u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u>
                        </strong>
                      )}
                    </td>
                  </tr>
                  <tr className="p-0 m-0">
                    <th scope="row">{t('VisitReportsPDF.header.visit-start-time')}</th>
                    <td className="text-start p-1 m-0">
                      {data?.timer_start ? (
                        dayjs(data?.timer_start).format('HH:mm')
                      ) : (
                        <strong>
                          <u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u>:
                          <u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u>
                        </strong>
                      )}
                    </td>
                  </tr>
                  <tr className="p-0 m-0">
                    <th scope="row">{t('VisitReportsPDF.header.visit-end-time')}</th>
                    <td className="text-start p-1 m-0">
                      {data?.timer_end ? (
                        dayjs(data?.timer_end).format('HH:mm')
                      ) : (
                        <strong>
                          <u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u>:
                          <u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u>
                        </strong>
                      )}
                    </td>
                  </tr>
                  <tr className="p-0 m-0">
                    <th scope="row">{t('VisitReportsPDF.header.visit-type')}</th>
                    <td className="text-start p-1 m-0">
                      <strong>
                        <u>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </u>
                      </strong>
                    </td>
                  </tr>
                  <tr className="p-0 m-0">
                    <th scope="row">{t('VisitReportsPDF.header.object')}</th>
                    <td className="text-start p-1 m-0" id="size-max">
                      <strong>{data?.object}</strong>
                    </td>
                  </tr>
                  <tr className="p-0 m-0">
                    <th scope="row">{t('VisitReportsPDF.header.fisical-person')}</th>
                    <td className="text-start p-1 m-0">
                      <strong>
                        <u>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </u>
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-4">
              <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle">
                <thead className="">
                  <tr>
                    <th className="text-center" scope="col">
                      {t('VisitReportsPDF.header.performer')}
                    </th>
                  </tr>
                </thead>
                <tbody className="list form-check-all" id="trap_table">
                  <tr className="align-content-center">
                    <td className="text-center">
                      <strong>
                        <u>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </u>
                      </strong>
                    </td>
                  </tr>
                  <tr className="align-content-center">
                    <td className="text-center">
                      <strong>
                        <u>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </u>
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
              <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle">
                <thead className="">
                  <tr>
                    <th className="text-center" scope="col">
                      {t('VisitReportsPDF.header.customer')}
                    </th>
                  </tr>
                </thead>
                <tbody className="list form-check-all" id="trap_table">
                  <tr className="align-content-center">
                    <td className="text-center">
                      <strong>
                        <u>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </u>
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="row mt-5">
            <div className="row text-center">
              <h5 className="mb-2">
                <strong>{t('VisitReportsPDF.table-enc.header')}</strong>
              </h5>
            </div>
            <div className="row">
              <div className="table-responsive">
                <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle">
                  <thead className="">
                    <tr>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.table-enc.enc-date')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.table-enc.pest-traces')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.table-enc.witnesses')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.table-enc.inspection-date')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.table-enc.inspector')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="list form-check-all" id="trap_table">
                    {data?.enc_data.length === 0 ? (
                      <tr className="align-content-center">
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                      </tr>
                    ) : (
                      data?.enc_data.map(enc => (
                        <tr className="align-content-center" key={enc.id}>
                          <td className="text-center">{enc.discovery_date}</td>
                          {enc.pest_name ? (
                            <td className="text-center">{enc.pest_name}</td>
                          ) : (
                            <td className="text-center">{enc.pest_traces}</td>
                          )}
                          <td className="text-center">{enc.witness}</td>
                          {enc.inspection_date === 'Не інспектовано' ? (
                            <td className="text-center text-danger">
                              {dayjs(enc.inspection_date).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')}
                            </td>
                          ) : (
                            <td className="text-center">
                              {dayjs(enc.inspection_date).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')}
                            </td>
                          )}
                          {enc.inspector === 'Не інспектовано' ? (
                            <td className="text-center text-danger">{enc.inspector}</td>
                          ) : (
                            <td className="text-center">{enc.inspector}</td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="row text-center">
              <h5 className="mb-2">
                <strong>{t('VisitReportsPDF.table-traps.header')}</strong>
              </h5>
            </div>
            <div className="row">
              <div className="table-responsive">
                <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle">
                  <thead className="">
                    <tr>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.table-traps.sequence-number')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.table-traps.serial-number')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.table-traps.last-message')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.table-traps.next-message')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.table-traps.amount-of-lost-msgs')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="list form-check-all" id="trap_table">
                    {data?.traps_data.length === 0 ? (
                      <tr className="align-content-center">
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                      </tr>
                    ) : (
                      data?.traps_data.map(trap => (
                        <tr className="align-content-center" key={trap.id}>
                          <td className="text-center">{trap.trap_number}</td>
                          <td className="text-center">{trap.serial_number}</td>
                          <td className="text-center">
                            {dayjs(trap.offline_start).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')}
                          </td>
                          <td className="text-center">
                            {dayjs(trap.offline_end).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')}
                          </td>
                          <td className="text-center">{trap.lost_msgs}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="row border-bottom-dashed border-light my-3" />
          {!isLoading && data?.start_date && data?.end_date && (
            <TrapTimeLineChart
              slug={slug || ''}
              trapType={TrapType.MCT}
              startDate={data.start_date}
              endDate={data.end_date}
            />
          )}
          <div className="row border-bottom-dashed border-light my-3" />
          {!isLoading && data?.start_date && data?.end_date && (
            <SBSChart slug={slug || ''} startDate={data.start_date} endDate={data.end_date} />
          )}
          {!isLoading && data?.start_date && data?.end_date && (
            <TrapTimeLineChart
              slug={slug || ''}
              trapType={TrapType.SMST}
              startDate={data.start_date}
              endDate={data.end_date}
            />
          )}
          {!isLoading && data?.start_date && data?.end_date && (
            <TrapTimeLineChart
              slug={slug || ''}
              trapType={TrapType.SRST}
              startDate={data.start_date}
              endDate={data.end_date}
            />
          )}
          <div className="row border-bottom-dashed border-light my-3" />
          <HeatMaps startDate={data?.start_date || ''} endDate={data?.end_date || ''} />
          <div className="row mt-2">
            <div className="row text-center">
              <h5 className="mb-2">
                <strong>{t('VisitReportsPDF.vuls-table.header')}</strong>
              </h5>
            </div>
            <div className="row">
              <div className="table-responsive">
                <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle">
                  <thead className="">
                    <tr>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.vuls-table.type')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.vuls-table.criticality')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.vuls-table.responsible')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.vuls-table.inspector')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.vuls-table.create-date')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.vuls-table.last-date')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.vuls-table.elimination-date')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="list form-check-all" id="trap_table">
                    {data?.vuls_data.length === 0 ? (
                      <tr className="align-content-center">
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                      </tr>
                    ) : (
                      data?.vuls_data.map(vul => (
                        <tr className="align-content-center" key={vul.id}>
                          <td className="text-center">{vul.category}</td>
                          {vul.criticality === 'Критичний' ? (
                            <td className="text-center text-danger">{vul.criticality}</td>
                          ) : (
                            <td className="text-center">{vul.criticality}</td>
                          )}
                          <td className="text-center">{vul.responsible}</td>
                          <td className="text-center">{vul.inspector}</td>
                          <td className="text-center">{vul.create_date}</td>
                          <td className="text-center">{vul.last_date}</td>
                          {vul.fix_date === 'Не усунено!' ? (
                            <td className="text-center text-danger">{vul.fix_date}</td>
                          ) : (
                            <td className="text-center">{vul.fix_date}</td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="row text-center">
              <h5 className="mb-2">
                <strong>{t('VisitReportsPDF.poison-table.header')}</strong>
              </h5>
            </div>
            <div className="row">
              <div className="table-responsive">
                <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle w-100">
                  <thead className="">
                    <tr>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.poison-table.product-used')}
                      </th>
                      <th className="text-center" style={{ width: '100px' }} scope="col">
                        {t('VisitReportsPDF.poison-table.amount')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.poison-table.usage-method')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.poison-table.usage-reason')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.poison-table.usage-place')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.poison-table.person')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="list form-check-all">
                    {data?.poison_usage.length === 0 ? (
                      <tr className="align-content-center">
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                      </tr>
                    ) : (
                      data?.poison_usage.map(poison => (
                        <tr className="align-content-center" key={poison.id}>
                          <td className="text-center">{poison.poison_name}</td>
                          <td className="text-center">
                            {poison.poison_amount} {poison.amount_type}{' '}
                          </td>
                          <td className="text-center">{poison.usage_method}</td>
                          <td className="text-center">{poison.usage_reason}</td>
                          <td className="text-center">{poison.usage_place}</td>
                          <td className="text-center">{poison.added_by}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="row text-center">
              <h5 className="mb-2">
                <strong>{t('VisitReportsPDF.monitoring-table.header')}</strong>
              </h5>
            </div>
            <div className="row">
              <div className="table-responsive">
                <table className="table table-bordered dt-responsive table-striped table-sm table-hover align-middle">
                  <thead>
                    <tr>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.monitoring-table.trap-number')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.monitoring-table.trap-type')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.monitoring-table.trap-serial-number')}
                      </th>
                      <th className="text-center install-date" scope="col">
                        {t('VisitReportsPDF.monitoring-table.install-date')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.monitoring-table.remove-date')}
                      </th>
                      <th className="text-center signal-date" scope="col">
                        {t('VisitReportsPDF.monitoring-table.signal-dates')}
                      </th>
                      <th className="text-center signal-time" scope="col">
                        {t('VisitReportsPDF.monitoring-table.signal-time')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="list form-check-all">
                    {data?.monitoring_data
                      .sort((a, b) => (a.trap_number ?? 0) - (b.trap_number ?? 0))
                      .map(monitoring => {
                        const signals = monitoring.signals_data ? JSON.parse(monitoring.signals_data) : [];

                        return signals.length > 0 ? (
                          signals.map((signal: SignalData, signalIndex: number) => {
                            const signalTime = Array.isArray(signal.time) ? signal.time : JSON.parse(signal.time);

                            return signalTime.map((time: SignalTimeData, timeIndex: number) => (
                              <tr className="align-content-center" key={`${monitoring.id}-${signalIndex}-${timeIndex}`}>
                                {signalIndex === 0 && timeIndex === 0 && (
                                  <>
                                    <td
                                      className="text-center"
                                      rowSpan={signals.reduce(
                                        (acc: number, signal: { time: SignalTimeData[] }) => acc + signal.time.length,
                                        0,
                                      )}
                                    >
                                      {monitoring.trap_number}
                                    </td>
                                    <td
                                      className="text-center"
                                      rowSpan={signals.reduce(
                                        (acc: number, signal: { time: SignalTimeData[] }) => acc + signal.time.length,
                                        0,
                                      )}
                                    >
                                      {monitoring.trap_type}
                                    </td>
                                    <td
                                      className="text-center"
                                      rowSpan={signals.reduce(
                                        (acc: number, signal: { time: SignalTimeData[] }) => acc + signal.time.length,
                                        0,
                                      )}
                                    >
                                      {monitoring.trap_serial_number}
                                    </td>
                                    <td
                                      className="text-center install-date"
                                      rowSpan={signals.reduce(
                                        (acc: number, signal: { time: SignalTimeData[] }) => acc + signal.time.length,
                                        0,
                                      )}
                                    >
                                      {monitoring.install_date}
                                    </td>
                                    <td
                                      className="text-center"
                                      rowSpan={signals.reduce(
                                        (acc: number, signal: { time: SignalTimeData[] }) => acc + signal.time.length,
                                        0,
                                      )}
                                    >
                                      {monitoring.remove_date}
                                    </td>
                                  </>
                                )}
                                {timeIndex === 0 && (
                                  <td className="text-center signal-date" rowSpan={signalTime.length}>
                                    {dayjs(signal.date).format('YYYY-MM-DD')}
                                  </td>
                                )}
                                <td className="text-center signal-time">{dayjs(time.inner_time).format('HH:mm:ss')}</td>
                              </tr>
                            ));
                          })
                        ) : (
                          <tr key={monitoring.id}>
                            <td className="text-center">{monitoring.trap_number || 'N/A'}</td>
                            <td className="text-center">{monitoring.trap_type}</td>
                            <td className="text-center">{monitoring.trap_serial_number}</td>
                            <td className="text-center install-date">{monitoring.install_date}</td>
                            <td className="text-center">{monitoring.remove_date}</td>
                            <td className="text-center text-danger" colSpan={2}>
                              No Activity
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="row text-center">
              <h5 className="mb-2">
                <strong>{t('MonitoringBait.mainTitle')}</strong>
              </h5>
            </div>
            <div className="row">
              <div className="table-responsive">
                <table className="table table-bordered dt-responsive table-striped table-sm table-hover align-middle">
                  <thead>
                    <tr>
                      <th className="text-center" scope="col">
                        {t('MonitoringBait.chieldAccordion.sequenceNumber')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('MonitoringBait.chieldAccordion.trapType')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('MonitoringBait.chieldAccordion.usedRodenticide')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.monitoring-bait-table.signalDate')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.monitoring-bait-table.signalTime')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.monitoring-bait-table.RodenticideAmount')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.monitoring-bait-table.added')}
                      </th>
                      <th className="text-center" scope="col">
                        {t('VisitReportsPDF.monitoring-bait-table.eaten')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="list form-check-all">
                    {data?.monitoring_bait_data.map(monitoring =>
                      monitoring.signals_data && monitoring.signals_data !== '[]' ? (
                        JSON.parse(monitoring.signals_data).map((signal: SignalBaitData, index: number) =>
                          signal.time.map((time, timeIndex) => (
                            <tr className="align-content-center" key={`${monitoring.id}-${index}-${timeIndex}`}>
                              {index === 0 && timeIndex === 0 && (
                                <>
                                  <td className="text-center" rowSpan={signal.time.length}>
                                    {monitoring.trap_number || 'N/A'}
                                  </td>
                                  <td className="text-center" rowSpan={signal.time.length}>
                                    {monitoring.trap_type}
                                  </td>
                                  <td className="text-center" rowSpan={signal.time.length}>
                                    {monitoring.used_poison}
                                  </td>
                                </>
                              )}
                              {timeIndex === 0 && (
                                <td className="text-center signal-date" rowSpan={signal.time.length}>
                                  {dayjs(signal.date).format('YYYY-MM-DD')}
                                </td>
                              )}
                              <td className="text-center signal-time">{dayjs(time.inner_time).format('HH:mm:ss')}</td>
                              <td className="text-center">{time.bait_filling}</td>
                              <td className="text-center">{time.added_bait}</td>
                              <td className="text-center">{time.eaten_bait}</td>
                            </tr>
                          )),
                        )
                      ) : (
                        <tr key={monitoring.id}>
                          <td className="text-center">{monitoring.trap_number || 'N/A'}</td>
                          <td className="text-center">{monitoring.trap_type}</td>
                          <td className="text-center">{monitoring.used_poison}</td>
                          <td className="text-center text-danger" colSpan={5}>
                            No Activity
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <InnerSTMonitoring
            slug={slug || ''}
            trapType={TrapTypeMonitoring.SMST}
            startDate={data?.start_date || ''}
            endDate={data?.end_date || ''}
          />
          <InnerSTMonitoring
            slug={slug || ''}
            trapType={TrapTypeMonitoring.SRST}
            startDate={data?.start_date || ''}
            endDate={data?.end_date || ''}
          />
          <div className="row mt-5">
            <div className="row">
              <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle vertical-table">
                <tbody className="list form-check-all">
                  <tr>
                    <th scope="row">{t('VisitReportsPDF.footer.report-generated')}</th>
                    <td className="text-start p-1 m-0">
                      {dayjs(data?.call_date).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="row">
              <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle vertical-table">
                <tbody className="list form-check-all">
                  <tr>
                    <th scope="row">{t('VisitReportsPDF.footer.pest-technician-full-name')}</th>
                    <td className="text-center" style={{ width: '60%' }}>
                      <i>{data?.pest_signatured_by ? data?.pest_signatured_by.username : ''}</i>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{t('VisitReportsPDF.footer.signature')}</th>
                    <td className={data?.pest_signatured_by ? 'text-center' : 'text-start'} style={{ width: '60%' }}>
                      {data?.pest_signatured_by ? (
                        <img
                          style={{ maxHeight: '70px' }}
                          src={`${process.env.REACT_APP_BASE_DJANGO_URL}${data?.pest_signatured_by.signature}`}
                          alt="Pest employee signature"
                        />
                      ) : (
                        userData &&
                        ['pest_client_employee', 'pest_client_manager', 'pest_client', 'pest_owner'].includes(
                          userData.role,
                        ) &&
                        (userData.user_signature ? (
                          <MLButton
                            innerText={t('VisitReportsPDF.footer.btn-sign')}
                            isGhost
                            isItalic
                            icon="ri-quill-pen-fill fs-16"
                            color="success"
                            onClick={() => {
                              singVisitByEmployee({
                                signVisitByPestEmployee: { pest_signatured_by: null },
                                id: id || '',
                                slug: slug || '',
                              });
                            }}
                          />
                        ) : (
                          <MLButton
                            onClick={() => onManageSignatureModalOpen(slug || '', data?.id || 0, false, 'visit')}
                            innerText={t('VisitReportsPDF.footer.btn-add-sign')}
                            color="success"
                            isGhost
                            icon="ri-quill-pen-fill"
                            fontSize={14}
                          />
                        ))
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <SignatureModal refetch={refetch} />
            <div className="row">
              <table className="table table-bordered dt-responsive nowrap table-striped table-sm table-hover align-middle vertical-table">
                <tbody className="list form-check-all">
                  <tr>
                    <th scope="row">{t('VisitReportsPDF.footer.full-name-of-responsoble-person')} </th>
                    <td className="text-center" style={{ width: '60%' }}>
                      <i>{data?.customer_signatured_by ? data?.customer_signatured_by.username : ''}</i>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{t('VisitReportsPDF.footer.signature')}</th>
                    <td className={data?.pest_signatured_by ? 'text-center' : 'text-start'} style={{ width: '60%' }}>
                      {data?.customer_signature ? (
                        <img
                          style={{ maxHeight: '70px' }}
                          src={data?.customer_signature}
                          alt="Pest employee signature"
                        />
                      ) : userData &&
                        ['pest_client_employee', 'pest_client_manager', 'pest_client'].includes(userData.role) ? (
                        <MLButton
                          onClick={() => onManageSignatureModalOpen(slug || '', data?.id || 0, true, 'visit')}
                          innerText="Sign"
                          color="success"
                          isGhost
                          icon="ri-quill-pen-fill"
                          fontSize={14}
                        />
                      ) : (
                        <a
                          className="btn btn-ghost-success"
                          target="_blank"
                          href={`${process.env.REACT_APP_BASE_DJANGO_URL}/user/${userData?.slug}/update`}
                          rel="noopener noreferrer"
                        >
                          {t('VisitReportsPDF.footer.btn-add-sign')}
                        </a>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default VisitDocsPDF;
